// OrchestratorTab — Project-level orchestrator dashboard
import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getWhatNext, getAssessment, triggerAssessment } from '../../services/api'
import type { SuggestedAction, EcosystemMatch } from '../../types'
import AssessmentCards from './AssessmentCards'
import { deriveAssessmentState, INSUFFICIENT_BASIS_REASON } from './assessmentScores'
import WhatNextPanel from './WhatNextPanel'
import ProjectWhatNextPanel from './ProjectWhatNextPanel'
import EcosystemMatchesPanel from './EcosystemMatchesPanel'

interface OrchestratorTabProps {
  projectId: string
  documentIds?: string[]
}

export default function OrchestratorTab({ projectId, documentIds = [] }: OrchestratorTabProps) {
  const queryClient = useQueryClient()
  const [allActions, setAllActions] = useState<SuggestedAction[]>([])
  // Find Specialists writes results back into this state via onMatchesFound;
  // without the setter the panel rendered the empty state even after the
  // API returned 10+ matches (bug surfaced 23-May).
  const [ecosystemMatches, setEcosystemMatches] = useState<EcosystemMatch[]>([])
  const [loading, setLoading] = useState(true)

  // Demo UX findings (22 Aug 2026), items 1+3: this tab used to fetch the
  // assessment into private useState with its own private axis parser, so it
  // could show 72/68/75 while the Overview tab and the Workflow Progress
  // stepper — reading a separate react-query cache entry with a different
  // parser — said "Not assessed" on the very same page. It now shares the
  // ['assessment', projectId] query and the ONE normaliser with ProjectView,
  // so every view of the scores updates together, in the same render.
  const { data: assessmentData, isSuccess, isError } = useQuery({
    queryKey: ['assessment', projectId],
    queryFn: () => getAssessment(projectId),
    retry: 1,
  })
  const assessmentState = deriveAssessmentState(assessmentData)
  const scores = assessmentState.kind === 'scored' ? assessmentState.scores : undefined
  const isInsufficientBasis = assessmentState.kind === 'insufficient_basis'

  const triggerMutation = useMutation({
    mutationFn: () => triggerAssessment(projectId),
    onSuccess: (result) => {
      // Seed the shared cache with the fresh assessment, then refetch: the
      // stepper and Overview panel light up in the same moment this tab does —
      // no more "scores appear only after some later click forces a refresh".
      queryClient.setQueryData(['assessment', projectId], result)
      queryClient.invalidateQueries({ queryKey: ['assessment', projectId] })
    },
  })

  // Self-heal: if the project reached this tab with no assessment stored yet
  // (the analysis pipeline normally auto-creates one), trigger scoring once.
  // Story #6172 F11: this must key off assessmentState.kind, not `scores` —
  // an insufficient_basis record is a real, deliberate answer the engine
  // already gave (kind !== 'none'), and re-triggering it would burn a
  // Bedrock call for a result that would come back the same way every time.
  const triggeredFor = useRef<string | null>(null)
  const { mutate: runTrigger } = triggerMutation
  useEffect(() => {
    if (!(isSuccess || isError)) return
    if (assessmentState.kind !== 'none' || triggeredFor.current === projectId) return
    triggeredFor.current = projectId
    runTrigger()
  }, [isSuccess, isError, assessmentState.kind, projectId, runTrigger])

  // Aggregate what-next actions across all documents
  useEffect(() => {
    if (documentIds.length === 0) {
      setLoading(false)
      return
    }
    Promise.all(documentIds.map((id) => getWhatNext(id).catch(() => [] as SuggestedAction[])))
      .then((results) => setAllActions(results.flat()))
      .finally(() => setLoading(false))
  }, [documentIds])

  // Find the document that has the most pending actions (for WhatNextPanel)
  const bestDocId = (() => {
    if (documentIds.length === 0) return undefined
    const counts: Record<string, number> = {}
    for (const a of allActions) {
      if (a.status === 'pending') {
        counts[a.document_id] = (counts[a.document_id] || 0) + 1
      }
    }
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1])
    return sorted.length > 0 ? sorted[0][0] : documentIds[0]
  })()

  if (loading) {
    return <div className="p-4 text-sm text-gray-500">Loading orchestrator data...</div>
  }

  return (
    <div className="space-y-4 p-4">
      {/* Workflow Progress strip moved to the project-level header so it
          persists across every tab — see ProjectView.tsx. */}

      {/* Assessment Scores */}
      <AssessmentCards scores={scores} insufficientBasisReason={isInsufficientBasis ? INSUFFICIENT_BASIS_REASON : undefined} />

      {/* Project-level Strategic What Next */}
      <ProjectWhatNextPanel projectId={projectId} />

      {/* Split layout: actions left, unified matches right */}
      <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
        {/* Aggregated actions (5/7 width) */}
        <div className="lg:col-span-5 bg-white border rounded-lg p-3">
          <h3 className="text-xs font-semibold text-gray-700 mb-2">
            Project Suggestions ({allActions.filter((a) => a.status === 'pending').length} pending)
          </h3>
          {bestDocId ? (
            <WhatNextPanel documentId={bestDocId} projectId={projectId} />
          ) : (
            <p className="text-sm text-gray-500 italic">Upload documents to get AI suggestions.</p>
          )}
        </div>

        {/* Unified ecosystem + directory matches (2/7 width) */}
        <div className="lg:col-span-2 bg-white border rounded-lg p-3">
          <EcosystemMatchesPanel
            matches={ecosystemMatches}
            documentId={bestDocId}
            onMatchesFound={setEcosystemMatches}
          />
        </div>
      </div>
    </div>
  )
}
